import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { useVideoControlState } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import dayjs, { Dayjs } from "dayjs";
import { memo, useMemo } from "react";
import { useRailcarMovementsBetween } from "hooks/useRailcarMovements";
import { useThrottledValue } from "hooks/useThrottledValue";
import type { RailcarMovement } from "src/store/api/generatedApi";
import { useTenantTranslation } from "hooks/formatters";

const CURRENT_TIME_THROTTLE_MS = 500;

interface RailcarTimelineProps {
  videoStart: Dayjs;
  videoEnd: Dayjs;
}

const GROUP_GAP_MINUTES = 5; // maximum gap between railcars to consider them part of the same group

export enum GroupType {
  INBOUND = "inbound",
  OUTBOUND = "outbound",
}

export function groupByTime<T extends { time: Dayjs }>(
  items: T[],
  gapMinutes: number
) {
  const [first, ...rest] = items;
  if (!first) return [] as T[][];
  const groups: T[][] = [];
  let currentGroup: T[] = [first];
  let prev = first;
  for (const cur of rest) {
    const diff = cur.time.diff(prev.time, "minute");
    if (diff <= gapMinutes) {
      currentGroup.push(cur);
    } else {
      groups.push(currentGroup);
      currentGroup = [cur];
    }
    prev = cur;
  }
  groups.push(currentGroup);
  return groups;
}

const RailcarTimelineInner = ({
  videoStart,
  videoEnd,
}: RailcarTimelineProps) => {
  const { railcarMovements } = useRailcarMovementsBetween(videoStart, videoEnd);
  const { t } = useTenantTranslation();
  const { currentTime: currentTimeSeconds } = useVideoControlState();
  const throttledCurrentTimeSeconds = useThrottledValue(
    currentTimeSeconds,
    CURRENT_TIME_THROTTLE_MS
  );

  const fullSeconds = useMemo(
    () => videoEnd.diff(videoStart, "second") || 1,
    [videoStart, videoEnd]
  );

  const currentLeftPercent = useMemo(() => {
    const secs = Math.max(
      0,
      Math.min(fullSeconds, throttledCurrentTimeSeconds)
    );
    return Math.max(0, Math.min(100, (secs / fullSeconds) * 100));
  }, [throttledCurrentTimeSeconds, fullSeconds]);

  const inboundGroups = useMemo(() => {
    const inbound = railcarMovements.railcar_movements
      .map((r) => ({ r, time: dayjs(r.inbound_time) }))
      .sort((a, b) => a.time.diff(b.time));

    const grouped = groupByTime(inbound, GROUP_GAP_MINUTES);
    return grouped
      .filter((group) =>
        group.some(
          (g) => g.time.isAfter(videoStart) && g.time.isBefore(videoEnd)
        )
      )
      .map((group) => group.map((g) => g.r));
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- use .valueOf() to avoid new Dayjs object identity on every render
  }, [railcarMovements, videoStart.valueOf(), videoEnd.valueOf()]);

  const outboundGroups = useMemo(() => {
    const outbound = railcarMovements.railcar_movements
      .filter((r) => r.outbound_time != null)
      .map((r) => ({ r, time: dayjs(r.outbound_time) }))
      .sort((a, b) => a.time.diff(b.time));

    const grouped = groupByTime(outbound, GROUP_GAP_MINUTES);
    return grouped
      .filter((group) =>
        group.some(
          (g) => g.time.isAfter(videoStart) && g.time.isBefore(videoEnd)
        )
      )
      .map((group) => group.map((g) => g.r));
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- use .valueOf() to avoid new Dayjs object identity on every render
  }, [railcarMovements, videoStart.valueOf(), videoEnd.valueOf()]);

  const renderMarker = (railcarGroup: RailcarMovement[], type: GroupType) => {
    const times = railcarGroup.map((r) =>
      type === GroupType.INBOUND
        ? dayjs(r.inbound_time)
        : dayjs(r.outbound_time)
    );

    const leftPercent = Math.max(
      0,
      Math.min(
        100,
        ((times[0]?.diff(videoStart, "second") ?? 0) / fullSeconds) * 100
      )
    );
    // compute the end percent relative to the same videoStart reference, then
    // derive a width in percent so the marker spans between left and end.
    const endPercent = Math.max(
      0,
      Math.min(
        100,
        ((times[times.length - 1]?.diff(videoStart, "second") ?? 0) /
          fullSeconds) *
          100
      )
    );
    let widthPercent = endPercent - leftPercent;
    // ensure visible minimum and that the marker does not overflow the right edge
    widthPercent = Math.max(0.5, Math.min(widthPercent, 100 - leftPercent));

    const counts = railcarGroup.reduce<Record<string, number>>((acc, r) => {
      const name = r.material.labelling_name ?? r.material.name;
      acc[name] = (acc[name] ?? 0) + 1;
      return acc;
    }, {});

    const tooltipContent = (
      <Box>
        {railcarGroup.map((r) => {
          const time =
            type === GroupType.INBOUND
              ? dayjs(r.inbound_time)
              : dayjs(r.outbound_time);
          const tagIdDisplay = r.tag_id_display ?? r.tag_id;
          return (
            <Typography
              key={r.tag_id}
              variant="caption"
              sx={{ display: "block" }}
            >
              {time.utc().format("HH:mm")} —{" "}
              {r.material.labelling_name ?? r.material.name} ({tagIdDisplay})
            </Typography>
          );
        })}
      </Box>
    );

    return (
      <Tooltip
        key={`${type}-${leftPercent}-${widthPercent}`}
        title={tooltipContent}
        arrow
      >
        <Stack
          sx={{
            position: "absolute",
            left: `${leftPercent}%`,
            bottom: type === GroupType.INBOUND ? "50%" : "auto",
            top: type === GroupType.OUTBOUND ? "50%" : "auto",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            flexDirection:
              type === GroupType.INBOUND ? "column" : "column-reverse",
            pointerEvents: "auto",
          }}
        >
          {Object.entries(counts).map(([k, v]) => (
            <Box
              key={`${type}-${k}-${v}`}
              sx={{
                width: "100%",
                borderRadius: 1,
                bgcolor:
                  type === GroupType.INBOUND ? "success.main" : "error.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 0.5,
              }}
            >
              <Typography
                variant="caption"
                color="common.white"
                noWrap
                sx={{ fontWeight: 600, fontSize: "0.55rem" }}
              >
                {/* eslint-disable-next-line i18next/no-literal-string */}
                {v}x {k}
              </Typography>
            </Box>
          ))}
          <Stack
            direction="row"
            width="100%"
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "auto", // push wheels to the bottom of the marker container
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: "grey.900",
              }}
            />
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: "grey.900",
              }}
            />
          </Stack>
        </Stack>
      </Tooltip>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          position: "relative",
          height: 100,
          bgcolor: "background.paper",
          borderRadius: 1,
        }}
      >
        {/* base central thin line */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "50%",
            height: 2,
            transform: "translateY(-50%)",
            bgcolor: "divider",
          }}
        />
        {/* current time vertical line */}
        <Box
          sx={{
            position: "absolute",
            left: `${currentLeftPercent}%`,
            height: "100%",
            width: "2px",
            bgcolor: "text.primary",
            opacity: 0.9,
            zIndex: 1,
          }}
        />
        {inboundGroups.map((g) => renderMarker(g, GroupType.INBOUND))}
        {outboundGroups.map((g) => renderMarker(g, GroupType.OUTBOUND))}
        <Typography
          key="inbound-label"
          variant="caption"
          color="success.main"
          fontWeight={600}
          sx={{
            textAlign: "center",
            position: "absolute",
            left: 0,
            top: 0,
          }}
        >
          {t("inbound")}
        </Typography>
        <Typography
          key="outbound-label"
          variant="caption"
          color="error.main"
          fontWeight={600}
          sx={{
            textAlign: "center",
            position: "absolute",
            left: 0,
            bottom: 0,
          }}
        >
          {t("outbound")}
        </Typography>
      </Box>
    </Box>
  );
};

export const RailcarTimeline = memo(RailcarTimelineInner);

export default RailcarTimeline;
